import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Snackbar, Alert } from "@mui/material";
import { GoogleMap, useJsApiLoader, Marker, Polyline } from '@react-google-maps/api'
import { MousePointer2, Plus, Minus, Heart, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { deriveGpsStatus, gpsLevelColor } from '../utils/gpsStatus';

const mapOptions = {
  zoomControl: false,
  fullscreenControl: false,
  mapTypeId: "satellite",
  mapTypeControl: false,
  streetViewControl: false,
  keyboardShortcuts: false,
  cameraControl: false,
  rotateControl: false,
  maxZoom: 22,
};

const toFiniteNumber = (value, fallback = null) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
};

const isValidLatLng = (point) =>
  !!point && Number.isFinite(point.lat) && Number.isFinite(point.lng);

// Diagnostic names arrive with the aggregator path; the last segment is the
// readable part: "/PowerTrain/DriveUnit hydraulic_system" -> "DriveUnit hydraulic_system".
const shortComponentName = (name) => String(name).split('/').filter(Boolean).pop() || String(name);

/**
 * MapScreen now receives pre-computed data from the robot-side aggregator.
 * Props:
 *   appData          - otto_msgs/MobileAppData (all scalars/strings pre-computed)
 *   globalPathMap     - [{lat, lng}, ...] already converted from UTM
 *   currentPathMap    - [{lat, lng}, ...] already converted from UTM (/curr_path; drawn only
 *                       when the robot does not publish the active path)
 *   activePathMap     - [{lat, lng}, ...] the path "now": current + next segment the robot is
 *                       tracking (mission run, go_to_start, RTH, resume); empty between runs
 *   activePathAvailable - true once the robot has published the active path on this connection
 *   dataStale         - { appData: bool, globalPath: bool, currentPath: bool }
 */
const StaleBadge = ({ show }) => show ? (
  <span style={{
    position: 'absolute',
    top: '2px',
    right: '4px',
    fontSize: '0.6rem',
    fontWeight: 'bold',
    color: 'white',
    backgroundColor: 'rgba(0,0,0,0.7)',
    padding: '1px 4px',
    borderRadius: '3px',
    lineHeight: 1,
  }}>STALE</span>
) : null;

const MapScreen = ({ appData, fusionStatus = null, safetyState = {}, estopLatched = null, globalPathMap = [], currentPathMap = [], activePathMap = [], activePathAvailable = false, dataStale = {}, dataSeq = 0, callSetStartNav, callManualSetLowerLink, callRobotEnable, callGoToStart, callSafetyReset }) => {

  // Heartbeat animation on new data
  const [heartBeat, setHeartBeat] = useState(false);
  const prevSeqRef = useRef(0);
  useEffect(() => {
    if (dataSeq !== prevSeqRef.current) {
      prevSeqRef.current = dataSeq;
      setHeartBeat(true);
      const timer = setTimeout(() => setHeartBeat(false), 300);
      return () => clearTimeout(timer);
    }
  }, [dataSeq]);

  const [isFollowRobot, setIsFollowRobot] = useState(true);
  const [zoom, setZoom] = useState(12);
  const [zoomInPressed, setZoomInPressed] = useState(false);
  const [zoomOutPressed, setZoomOutPressed] = useState(false);
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [safetyExpanded, setSafetyExpanded] = useState(true);
  const [goToStartSnack, setGoToStartSnack] = useState({ open: false, severity: 'success', message: '' });
  const [safetyResetSnack, setSafetyResetSnack] = useState({ open: false, severity: 'info', message: '' });
  const [safetyResetBusy, setSafetyResetBusy] = useState(false);
  // Last /safety_reset answer, kept on screen after the snackbar is gone:
  // { outcome: 'ok' | 'refused' | 'error', message, at }
  const [safetyResetResult, setSafetyResetResult] = useState(null);

  // Navigation start/stop — state comes from robot via appData.nav_running
  const isNavRunning = !!appData?.nav_running;
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null });

  const handleNavButton = useCallback(() => {
    setConfirmDialog({
      open: true,
      action: isNavRunning ? 'stop' : 'start',
    });
  }, [isNavRunning]);

  const handleCancel = useCallback(() => {
    setConfirmDialog({ open: false, action: null });
  }, []);

  // Planter raise/lower feedback
  const [planterBusy, setPlanterBusy] = useState(false);
  const [planterSnack, setPlanterSnack] = useState({ open: false, severity: 'info', message: '' });

  // Robot armed state from aggregated /mobile_app/data topic (robot_armed field)
  const armed = !!appData?.robot_armed;

  const handlePlanter = useCallback(async (targetPosition, label) => {
    if (!callManualSetLowerLink) return;
    setPlanterBusy(true);
    setPlanterSnack({ open: true, severity: 'info', message: `${label}: sending...` });
    try {
      const result = await callManualSetLowerLink(targetPosition, 15.0);
      setPlanterSnack({
        open: true,
        severity: result?.success ? 'success' : 'error',
        message: `${label}: ${result?.success ? 'OK' : 'FAILED'}${result?.message ? ` - ${result.message}` : ''}`,
      });
    } catch (e) {
      setPlanterSnack({ open: true, severity: 'error', message: `${label}: ${e?.message || 'service call failed'}` });
    } finally {
      setPlanterBusy(false);
    }
  }, [callManualSetLowerLink]);

  const handlePlanterSnackClose = useCallback(() => {
    setPlanterSnack((prev) => ({ ...prev, open: false }));
  }, []);

  const handleGoToStartSnackClose = useCallback(() => {
    setGoToStartSnack((prev) => ({ ...prev, open: false }));
  }, []);

  const handleGoToStart = useCallback(async () => {
    if (!callGoToStart) return;
    setGoToStartSnack({ open: true, severity: 'info', message: 'GO TO START: sending...' });
    try {
      const result = await callGoToStart();
      setGoToStartSnack({
        open: true,
        severity: result?.success ? 'success' : 'error',
        message: `GO TO START: ${result?.success ? 'OK' : 'FAILED'}`,
      });
    } catch (e) {
      setGoToStartSnack({ open: true, severity: 'error', message: `GO TO START: ${e?.message || 'service call failed'}` });
    }
  }, [callGoToStart]);

  // Safety reset: releases a latched e-stop / safety stop on the robot. The
  // robot's answer is shown verbatim — on a refusal it says why (e.g. an
  // emergency-level fault is still active), and the person at the machine has
  // no other way to see that. Failures stay on screen until dismissed.
  const handleSafetyReset = useCallback(async () => {
    if (!callSafetyReset) return;
    setSafetyResetBusy(true);
    setSafetyResetSnack({ open: true, severity: 'info', message: 'SAFETY RESET: sending...' });
    let outcome;
    let message;
    try {
      const result = await callSafetyReset();
      outcome = result?.success ? 'ok' : 'refused';
      message = result?.message || (result?.success ? 'Safety state reset' : 'no reason given');
    } catch (e) {
      // roslib hands a failed call (e.g. no such service on an older robot
      // build) to the error callback as a plain string, not an Error.
      outcome = 'error';
      message = (typeof e === 'string' ? e : e?.message) || 'service call failed';
    }
    setSafetyResetResult({ outcome, message, at: new Date() });
    setSafetyResetSnack({
      open: true,
      severity: outcome === 'ok' ? 'success' : 'error',
      message: outcome === 'ok' ? `SAFETY RESET: OK - ${message}`
        : outcome === 'refused' ? `SAFETY RESET refused: ${message}`
        : `SAFETY RESET failed: ${message}`,
    });
    setSafetyResetBusy(false);
  }, [callSafetyReset]);

  const handleSafetyResetSnackClose = useCallback((event, reason) => {
    // A tap elsewhere on the map must not dismiss a refusal unread.
    if (reason === 'clickaway') return;
    setSafetyResetSnack((prev) => ({ ...prev, open: false }));
  }, []);

  const executeArmToggle = useCallback(async (next) => {
    if (!callRobotEnable) return;
    const label = next ? 'ARM' : 'DISARM';
    setPlanterBusy(true);
    setPlanterSnack({ open: true, severity: 'info', message: `${label}: sending...` });
    try {
      const result = await callRobotEnable(next);
      setPlanterSnack({
        open: true,
        severity: result?.success ? 'success' : 'error',
        message: `${label}: ${result?.success ? 'OK' : 'FAILED'}${result?.message ? ` - ${result.message}` : ''}`,
      });
    } catch (e) {
      setPlanterSnack({ open: true, severity: 'error', message: `${label}: ${e?.message || 'service call failed'}` });
    } finally {
      setPlanterBusy(false);
    }
  }, [callRobotEnable]);

  const handleConfirm = useCallback(async () => {
    const action = confirmDialog.action;
    setConfirmDialog({ open: false, action: null });
    try {
      if (action === 'start' || action === 'stop') {
        await callSetStartNav(action === 'start');
      } else if (action === 'raise') {
        await handlePlanter(1, 'RAISE');
      } else if (action === 'lower') {
        await handlePlanter(2, 'LOWER');
      } else if (action === 'goToStart') {
        await handleGoToStart();
      } else if (action === 'arm' || action === 'disarm') {
        await executeArmToggle(action === 'arm');
      } else if (action === 'safetyReset') {
        await handleSafetyReset();
      }
    } catch (e) {
      console.error(`Failed to execute confirmed action "${action}":`, e);
    }
  }, [confirmDialog.action, callSetStartNav, handlePlanter, handleGoToStart, executeArmToggle, handleSafetyReset]);

  const handleArmToggle = useCallback(() => {
    setConfirmDialog({ open: true, action: armed ? 'disarm' : 'arm' });
  }, [armed]);

  // Measure status panel height dynamically via callback ref + ResizeObserver
  const [statusPanelHeight, setStatusPanelHeight] = useState(110);
  const statusObserverRef = useRef(null);
  const statusPanelRef = useCallback((node) => {
    if (statusObserverRef.current) {
      statusObserverRef.current.disconnect();
      statusObserverRef.current = null;
    }
    if (!node) return;
    setStatusPanelHeight(node.getBoundingClientRect().height);
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setStatusPanelHeight(entry.contentRect.height);
      }
    });
    observer.observe(node);
    statusObserverRef.current = observer;
  }, []);

  // Dynamic polyline styles based on zoom
  const dynamicOptions_yellow = useMemo(() => {
    const baseWeight = 2;
    const multiplier = 1.5;
    const strokeWeight = baseWeight + (20 - zoom) * multiplier;
    return {
      strokeColor: "yellow",
      strokeOpacity: 1,
      strokeWeight: Math.max(1, strokeWeight),
      clickable: false,
      draggable: false,
      editable: false,
      visible: true,
      zIndex: 11,
    };
  }, [zoom]);

  const dynamicOptions_green = useMemo(() => {
    const baseWeight = 2;
    const multiplier = 1.5;
    const strokeWeight = baseWeight + (20 - zoom) * multiplier;
    return {
      strokeColor: "cyan",
      strokeOpacity: 1,
      strokeWeight: Math.max(1, strokeWeight),
      clickable: false,
      draggable: false,
      editable: false,
      visible: true,
      zIndex: 11,
    };
  }, [zoom]);

  // Active path (the path "now": current + next segment): green, drawn on top of the yellow
  // global path and narrower, so the plan shows as a yellow edge around it.
  const dynamicOptions_active = useMemo(() => {
    const globalWeight = Math.max(1, 2 + (20 - zoom) * 1.5);   // same as dynamicOptions_yellow
    return {
      strokeColor: "#00e676",
      strokeOpacity: 1,
      strokeWeight: Math.max(2, Math.round(globalWeight * 0.6)),
      clickable: false,
      draggable: false,
      editable: false,
      visible: true,
      zIndex: 12,
    };
  }, [zoom]);

  // Robot position from pre-computed lat/lng
  const robotPosition = useMemo(() => {
    const lat = toFiniteNumber(appData?.latitude);
    const lng = toFiniteNumber(appData?.longitude);
    return isValidLatLng({ lat, lng }) ? { lat, lng } : null;
  }, [appData?.latitude, appData?.longitude]);

  // First pose of global path for start marker
  const firstPose = useMemo(() => {
    return globalPathMap.length > 0 && isValidLatLng(globalPathMap[0]) ? globalPathMap[0] : null;
  }, [globalPathMap]);

  // Initial center
  const initialCenter = {
    lat: toFiniteNumber(appData?.latitude, 42.021893),
    lng: toFiniteNumber(appData?.longitude, -93.639309),
  };
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: "AIzaSyCnPO-kAcC95Dl-t-8fNwOpk6UY1fhnpL4"
  });

  // eslint-disable-next-line no-unused-vars
  const [map, setMap] = useState(null);

  const onLoad = React.useCallback(function callback(map) {
    map.setCenter(initialCenter);
    map.setZoom(18);
    setMap(map);
    setZoom(18);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onZoomChanged = React.useCallback(() => {
    if (map) {
      const currentZoom = map.getZoom();
      setZoom(currentZoom);
    }
  }, [map]);

  const onUnmount = React.useCallback(function callback(map) {
    setMap(null);
  }, []);

  // Heading comes fully pre-computed from aggregator (includes icon rotation offset)
  const headingDeg = appData?.heading_deg || 0;

  // Marker icon — rotation from pre-computed heading
  const icon = useMemo(() => ({
    path: "M 0,0 L 10,0 M 10,0 L 7,-3 M 10,0 L 7,3",
    fillColor: "red",
    fillOpacity: 1,
    strokeColor: "red",
    strokeWeight: 3,
    scale: 3,
    anchor: { x: 0, y: 0 },
    rotation: headingDeg,
  }), [headingDeg]);

  // First pose icon — magenta square marking the start of the navigation line
  // (square shape + high-contrast color stand out against satellite imagery,
  // the cyan/yellow path, and the red robot-pose arrow)
  const firstPoseIcon = useMemo(() => {
    if (!window.google?.maps) return null;
    return {
      // Unit square centered at the origin; scale sets the half-side in px.
      path: "M -1 -1 H 1 V 1 H -1 Z",
      fillColor: "#FF00FF",
      fillOpacity: 1,
      strokeColor: "#FFFFFF",
      strokeWeight: 2,
      scale: 6,
      anchor: new window.google.maps.Point(0, 0),
    };
  }, [isLoaded]);

  // Small dot at the robot's exact location (rendered under/with the arrow)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const robotPointIcon = useMemo(() => {
    if (!window.google?.maps) return null;
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: "red",
      fillOpacity: 1,
      strokeColor: "red",
      strokeWeight: 0,
      scale: 4,
    };
  }, [isLoaded]);

  // Rear-axle marker (blue dot): a FIXED 1.98 m behind the red dot, along the robot heading.
  // heading_deg = -ENU_yaw (the aggregator negates it for the map icon), so the robot's
  // front/travel direction in geographic (East, North) is (cos h, -sin h) and the rear axle sits
  // 1.98 m opposite it. Offset in metres -> lat/lng (flat-earth, exact enough at this scale).
  //
  // Measured against salinv5 2026-08-12: renders 1.98000 m at bearing 264.183 deg vs 264.138
  // for dead astern (0.045 deg, sample timing) -- the trig and the -ENU_yaw convention check out.
  //
  // Two caveats worth knowing before trusting this as the control point:
  //   - The red dot is /fixposition/odometry_llh (frame FP_POI), which measured out as the
  //     GNSS ANTENNA MIDPOINT (gnss1 -0.011 m fore / -0.132 m port, gnss2 +0.031/+0.158,
  //     0.293 m baseline) -- NOT the front axle.
  //   - 1.98 duplicates otto.yaml `path_tracker.axle.rear_axle_ref_distance`, which is itself
  //     the wheelbase used as an uncalibrated default ("calibrate to the true GPS mount").
  //     If that ever gets calibrated, change it BOTH here and in otto.yaml or this marker will
  //     silently disagree with the point the controller actually tracks.
  const REAR_AXLE_OFFSET_M = 1.98;
  const rearAxlePosition = useMemo(() => {
    if (!robotPosition) return null;
    const h = (headingDeg * Math.PI) / 180;
    const dEast = -REAR_AXLE_OFFSET_M * Math.cos(h);   // rear = opposite the front direction
    const dNorth = REAR_AXLE_OFFSET_M * Math.sin(h);
    const dLat = dNorth / 111320;
    const dLng = dEast / (111320 * Math.cos((robotPosition.lat * Math.PI) / 180));
    return { lat: robotPosition.lat + dLat, lng: robotPosition.lng + dLng };
  }, [robotPosition, headingDeg]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rearAxlePointIcon = useMemo(() => {
    if (!window.google?.maps) return null;
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: "blue",
      fillOpacity: 1,
      strokeColor: "blue",
      strokeWeight: 0,
      scale: 4,
    };
  }, [isLoaded]);

  // Follow robot — smooth pan so robot appears at 70% from top
  useEffect(() => {
    if (robotPosition && isFollowRobot && map) {
      const projection = map.getProjection();
      if (projection) {
        const mapDiv = map.getDiv();
        const mapHeight = mapDiv.offsetHeight;
        const offsetPixels = mapHeight * 0.20;
        const scale = Math.pow(2, map.getZoom());
        const worldPoint = projection.fromLatLngToPoint(
          new window.google.maps.LatLng(robotPosition.lat, robotPosition.lng)
        );
        const offsetCenter = projection.fromPointToLatLng(
          new window.google.maps.Point(worldPoint.x, worldPoint.y - offsetPixels / scale)
        );
        map.panTo({ lat: offsetCenter.lat(), lng: offsetCenter.lng() });
      } else {
        map.panTo(robotPosition);
      }
    }
  }, [robotPosition, isFollowRobot, map, zoom]);

  // Pre-computed values from aggregator
  const speed = appData?.speed_mph || 0;
  const cteInches = appData?.cte_inches || 0;
  const heDegrees = appData?.he_degrees || 0;
  // Display-rounded values (so the direction note never contradicts a shown 0.0)
  const cteInchesRounded = Math.round(cteInches * 10) / 10;
  const heDegreesRounded = Math.round(heDegrees * 10) / 10;
  // Human-readable direction notation. Sign convention is left untouched:
  //   cte_inches > 0  => robot is to the RIGHT of the path  (< 0 => LEFT)
  //   he_degrees > 0  => robot heading is CW of the path    (< 0 => CCW)
  const cteSide = cteInchesRounded > 0 ? 'RIGHT of path'
                : cteInchesRounded < 0 ? 'LEFT of path' : '';
  const heSpin = heDegreesRounded > 0 ? 'CW of path'
               : heDegreesRounded < 0 ? 'CCW of path' : '';
  // GPS status is derived from the FUSION state, not from appData.gps_status.
  // The aggregator builds that string from the two GNSS receivers alone, so it
  // reported "RTK Fixed" while fusion was uninitialised and the robot published
  // lat/lng 0,0. See utils/gpsStatus.js. Falls back to the aggregator string
  // (flagged unverified) when the fusion topic is absent or has gone stale.
  const gps = deriveGpsStatus(
    dataStale.fusion ? null : fusionStatus,
    appData?.gps_status || "No Data",
  );
  const toolBarStatusLabel = appData?.toolbar_status || "UNKNOWN";
  const vacuumPressure = appData?.vacuum_pressure || 0;
  const engineRpm = appData?.engine_rpm || 0;
  const cmdLinearX = appData?.cmd_linear_x || 0;
  const cmdAngularZ = appData?.cmd_angular_z || 0;
  const motorTemps = appData?.motor_temps || [0, 0, 0, 0];
  const cpuTemp = appData?.cpu_temp || 0;   // Jetson CPU temp (0 when the robot doesn't publish it)

  // Stale data — single flag since all frequent data comes from one topic
  const isDataStale = dataStale.appData;

  // Safety speed reduction. path_tracker_v5 caps the speed at min_linear /
  // min_linear_turn while "REDUCE_SPEED" is in /safety_state active_actions
  // (safety_state_callback), so this uses exactly the same test.
  // 'active' | 'off' | 'unknown' (/safety_state has gone quiet; the robot keeps
  // its last state then, so the app must not guess).
  const speedReduced = (safetyState?.active_actions || []).includes('REDUCE_SPEED');
  const speedLimit = dataStale.safetyState ? 'unknown' : speedReduced ? 'active' : 'off';
  // Why: SafetyState has no per-component priority, so name the error
  // components first, then the warnings.
  const speedLimitCauses = [
    ...(safetyState?.error_components || []).map((c) => `${shortComponentName(c)} (error)`),
    ...(safetyState?.warning_components || []).map((c) => `${shortComponentName(c)} (warning)`),
  ];
  const speedLimitReason = speedLimitCauses.length === 0 ? 'cause not reported'
    : speedLimitCauses.slice(0, 2).join(', ')
      + (speedLimitCauses.length > 2 ? ` +${speedLimitCauses.length - 2} more` : '');

  // Helper: returns stale overlay style for a given flag
  const staleStyle = (isStale) => isStale ? {
    opacity: 0.5,
    position: 'relative',
  } : {};


  const mapOverlay = (
    <Box
      sx={{
        position: 'relative',
        top: '0',
        left: '0',
        width: '100%',
        backgroundColor: 'rgba(255, 255, 255, 1)',
        padding: { xs: '8px 6px', sm: '15px' },
        borderRadius: '0 0 10px 10px',
        zIndex: 1000,
        boxSizing: 'border-box',
      }}
    >
      {/* Global stale data warning banner */}
      {isDataStale && (
        <Box sx={{
          backgroundColor: 'rgba(255, 80, 0, 0.9)',
          color: 'white',
          textAlign: 'center',
          padding: '4px 8px',
          borderRadius: '5px',
          marginBottom: '8px',
          fontSize: '0.85rem',
          fontWeight: 'bold',
          animation: 'pulse 1.5s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.6 },
          },
        }}>
          DATA STALE - Connection may be interrupted
        </Box>
      )}
      <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap'}}>

        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isDataStale ? 'rgba(180, 180, 180, 0.75)' : Math.abs(cteInches) > 4 ? 'rgba(255, 0, 0, 0.75)' : Math.abs(cteInches) > 2 ? 'rgba(255, 255, 0, 0.75)' : 'rgba(0, 255, 0, 0.75)',
          borderRadius: '5px',
          marginBottom: '8px',
          width: '49%',
          height: { xs: '62px', sm: '81px' },
          ...staleStyle(isDataStale),
        }}>
          <Typography sx={{ fontSize: { xs: '0.8rem', sm: '1rem' }, fontWeight: 'bold', color: 'black', lineHeight: 1, width: '100%', textAlign: 'center' }}>CTE</Typography>
          <Typography sx={{ fontSize: { xs: '1.275rem', sm: '1.8rem' }, fontWeight: 'bold', color: 'black', lineHeight: 1.2 }}>{cteInchesRounded.toFixed(1)} in, {(Math.round(cteInches * 2.54 * 10) / 10).toFixed(1)} cm</Typography>
          {cteSide && (
            <Typography sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' }, fontWeight: 'bold', color: 'rgba(0,0,0,0.75)', lineHeight: 1, textAlign: 'center' }}>{cteSide}</Typography>
          )}
          <StaleBadge show={isDataStale} />
        </Box>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isDataStale ? 'rgba(180, 180, 180, 0.75)' : Math.abs(heDegrees) > 4 ? 'rgba(255, 0, 0, 0.75)' : Math.abs(heDegrees) > 2 ? 'rgba(255, 255, 0, 0.75)' : 'rgba(0, 255, 0, 0.75)',
          borderRadius: '5px',
          marginBottom: '8px',
          width: '49%',
          height: { xs: '62px', sm: '81px' },
          ...staleStyle(isDataStale),
        }}>
          <Typography sx={{ fontSize: { xs: '0.8rem', sm: '1rem' }, fontWeight: 'bold', color: 'black', lineHeight: 1, width: '100%', textAlign: 'center' }}>HE</Typography>
          <Typography sx={{ fontSize: { xs: '1.275rem', sm: '1.8rem' }, fontWeight: 'bold', color: 'black', lineHeight: 1.2 }}>{heDegreesRounded.toFixed(1)}&deg;</Typography>
          {heSpin && (
            <Typography sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' }, fontWeight: 'bold', color: 'rgba(0,0,0,0.75)', lineHeight: 1, textAlign: 'center' }}>{heSpin}</Typography>
          )}
          <StaleBadge show={isDataStale} />
        </Box>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: gpsLevelColor(gps.level, isDataStale),
          borderRadius: '5px',
          marginBottom: '8px',
          width: '49%',
          height: { xs: '62px', sm: '81px' },
          ...staleStyle(isDataStale),
        }}>
          <Typography sx={{ fontSize: { xs: '0.8rem', sm: '1rem' }, fontWeight: 'bold', color: 'black', lineHeight: 1 }}>GPS Status</Typography>
          <Typography sx={{ fontSize: { xs: '1.275rem', sm: '1.8rem' }, fontWeight: 'bold', color: 'black', lineHeight: 1.2 }}>{gps.label}</Typography>
          {/* Why it is degraded — antenna fix states and correction status, so an
              operator can tell "fusion still initialising" from "lost the sky". */}
          {gps.detail && (
            <Typography sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' }, fontWeight: 'bold', color: 'rgba(0,0,0,0.75)', lineHeight: 1, textAlign: 'center' }}>{gps.detail}</Typography>
          )}
          <StaleBadge show={isDataStale} />
        </Box>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isDataStale ? 'rgba(180, 180, 180, 0.75)' : speed > 0.5 ? 'rgba(0, 255, 0, 0.75)' : speed > 3.5 ? 'rgba(255, 255, 0, 0.75)' : 'rgba(255, 0, 0, 0.75)',
          borderRadius: '5px',
          marginBottom: '8px',
          width: '49%',
          height: { xs: '62px', sm: '81px' },
          position: 'relative',
          // Outline sits in the gap around the tile: marks it without changing
          // its size or covering the full-width speed reading.
          ...(speedLimit === 'active' ? { outline: '3px solid #ff9800' } : {}),
          ...staleStyle(isDataStale),
        }}>
          {speedLimit === 'active' && (
            <span data-testid="speed-reduced-badge" style={{
              position: 'absolute',
              top: '2px',
              left: '4px',
              fontSize: '0.6rem',
              fontWeight: 'bold',
              color: 'black',
              backgroundColor: '#ff9800',
              padding: '1px 4px',
              borderRadius: '3px',
              lineHeight: 1,
            }}>REDUCED</span>
          )}
          <Typography sx={{ fontSize: { xs: '0.8rem', sm: '1rem' }, fontWeight: 'bold', color: 'black', lineHeight: 1 }}>Speed</Typography>
          <Typography sx={{ fontSize: { xs: '1.275rem', sm: '1.8rem' }, fontWeight: 'bold', color: 'black', lineHeight: 1.2 }}>{(Math.round(speed * 100) / 100).toFixed(2)} mph, {(Math.round(speed / 2.23694 * 100) / 100).toFixed(2)} m/s</Typography>
          <Typography sx={{ fontSize: { xs: '0.8rem', sm: '1rem' }, fontWeight: 'bold', color: 'black', lineHeight: 1 }}>lin: {(Math.round(cmdLinearX * 100) / 100).toFixed(2)} ang: {(Math.round(cmdAngularZ * 100) / 100).toFixed(2)}</Typography>
          <StaleBadge show={isDataStale} />
        </Box>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isDataStale ? 'rgba(180, 180, 180, 0.75)' : 'rgba(0, 153, 255, 0.75)',
          borderRadius: '5px',
          marginBottom: '8px',
          width: '49%',
          height: { xs: '62px', sm: '81px' },
          ...staleStyle(isDataStale),
        }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', columnGap: '6px', justifyItems: 'center' }}>
            <Typography sx={{ fontSize: { xs: '0.8rem', sm: '1rem' }, fontWeight: 'bold', color: 'black', lineHeight: 1, justifySelf: 'end' }}>Bar</Typography>
            <Box sx={{ width: '1.5px', height: '0.8rem', backgroundColor: 'rgba(0,0,0,0.4)' }} />
            <Typography sx={{ fontSize: { xs: '0.8rem', sm: '1rem' }, fontWeight: 'bold', color: 'black', lineHeight: 1, justifySelf: 'start' }}>Vac (inH2O)</Typography>
            <Typography sx={{ fontSize: { xs: '1.275rem', sm: '1.8rem' }, fontWeight: 'bold', color: 'black', lineHeight: 1.2, justifySelf: 'end' }}>{toolBarStatusLabel}</Typography>
            <Box sx={{ width: '2px', height: '1.4rem', backgroundColor: 'rgba(0,0,0,0.4)' }} />
            <Typography sx={{ fontSize: { xs: '1.275rem', sm: '1.8rem' }, fontWeight: 'bold', color: 'black', lineHeight: 1.2, justifySelf: 'start' }}>{(Math.round(vacuumPressure * 10) / 10).toFixed(1)}</Typography>
          </Box>
          <StaleBadge show={isDataStale} />
        </Box>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isDataStale ? 'rgba(180, 180, 180, 0.75)' : 'rgba(240, 240, 240, 0.9)',
          borderRadius: '5px',
          marginBottom: '8px',
          width: '49%',
          height: { xs: '62px', sm: '81px' },
          padding: '4px',
          // Hard stop so a 3-digit reading can never push a column past the
          // tile edge and back off-screen again.
          overflow: 'hidden',
          ...staleStyle(isDataStale),
        }}>
          <Typography sx={{ fontSize: { xs: '0.8rem', sm: '1rem' }, fontWeight: 'bold', color: 'black', lineHeight: 1 }}>Motors °C</Typography>
          {/* FOUR readings only. CPU temp lives in the Dev Tools panel instead.
              Each reading stacks its label ABOVE its value, which roughly halves
              the width a reading needs versus side-by-side.

              Why cpu moved out: this tile is 49% of the viewport with a FIXED
              height, and users run their phones with enlarged text. Measured at
              390px wide with 3-digit readings, five columns started overflowing
              at a 1.125x text scale and by 1.5x the values collided into an
              unreadable run ("100104981011 12") with cpu clipped at the tile
              edge -- which is exactly what the operator reported. Four columns
              buy back ~25% width each; the fifth reading had no room to exist
              here at any accessible text size. */}
          <Box sx={{ display: 'flex', gap: '4px', width: '100%', justifyContent: 'center', alignItems: 'flex-start' }}>
            {motorTemps.slice(0, 4).map((temp, i) => ({
              label: ['fl', 'fr', 'rl', 'rr'][i],
              value: temp,
              warn: 60,
              hot: 80,
            })).map(({ label, value, warn, hot, dividerBefore }) => (
              <Box key={label} sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flex: 1,
                minWidth: 0,
                ...(dividerBefore ? { borderLeft: '1.5px solid rgba(0,0,0,0.25)' } : {}),
              }}>
                <Typography sx={{
                  fontSize: { xs: '0.55rem', sm: '0.7rem' },
                  fontWeight: 'bold',
                  lineHeight: 1,
                  color: isDataStale ? 'grey' : 'black',
                }}>{label}</Typography>
                <Typography sx={{
                  fontSize: { xs: '1.05rem', sm: '1.5rem' },
                  fontWeight: 'bold',
                  lineHeight: 1.15,
                  color: isDataStale ? 'grey' : value > hot ? 'rgba(255, 0, 0, 1)' : value > warn ? 'rgba(200, 160, 0, 1)' : 'rgba(0, 160, 0, 1)',
                }}>{Math.round(value)}</Typography>
              </Box>
            ))}
          </Box>
          <StaleBadge show={isDataStale} />
        </Box>
      </div>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: '6px', width: '100%' }}>
        <Button
          variant="contained"
          onClick={handleArmToggle}
          disabled={planterBusy || !callRobotEnable}
          sx={{
            flex: 1,
            height: '45px',
            borderRadius: '5px',
            fontSize: { xs: '1.275rem', sm: '1.8rem' },
            fontWeight: 'bold',
            backgroundColor: armed ? '#f44336' : '#4caf50',
            '&:hover': { backgroundColor: armed ? '#d32f2f' : '#388e3c' },
          }}
        >
          {armed ? 'DISARM' : 'ARM'}
        </Button>
        <Button
          variant="contained"
          onClick={handleNavButton}
          sx={{
            flex: 1,
            height: '45px',
            borderRadius: '5px',
            fontSize: { xs: '1.275rem', sm: '1.8rem' },
            fontWeight: 'bold',
            backgroundColor: isNavRunning ? '#f44336' : '#4caf50',
            '&:hover': { backgroundColor: isNavRunning ? '#d32f2f' : '#388e3c' },
          }}
        >
          {isNavRunning ? 'STOP' : 'START'}
        </Button>
      </Box>
    </Box>
  );


const handleZoomIn = useCallback(() => {
  setZoomInPressed(true);
  if (map) {
    const currentZoom = map.getZoom();
    map.setZoom(Math.min(currentZoom + 1, 22));
  }
  setTimeout(() => setZoomInPressed(false), 200);
}, [map]);

const handleZoomOut = useCallback(() => {
  setZoomOutPressed(true);
  if (map) {
    const currentZoom = map.getZoom();
    map.setZoom(Math.max(currentZoom - 1, 1));
  }
  setTimeout(() => setZoomOutPressed(false), 200);
}, [map]);

  return (
    <Box sx={{ height: '100%', width: '100%', position: 'relative', overflow: 'hidden' }}>
      {/* Top Status Panel - Fixed Position */}
      <Box ref={statusPanelRef} sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1001,
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}>
        {mapOverlay}
      </Box>

      {/* Top Left Controls - positioned dynamically below status panel */}
      <Box sx={{
        position: 'fixed',
        top: `${statusPanelHeight + 10}px`,
        left: 'max(8px, env(safe-area-inset-left, 8px))',
        zIndex: 1002,
      }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isFollowRobot ? '#CCFFCC' : 'white',
              borderRadius: '10px',
              width: '40px',
              height: '40px',
            }}
            onClick={() => setIsFollowRobot(!isFollowRobot)}
          >
            <MousePointer2 size={30} color='black' strokeWidth={isFollowRobot ? 3 : 2}/>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: zoomInPressed ? '#CCFFCC' : 'white',
              borderRadius: '10px',
              width: '40px',
              height: '40px',
            }}
            onClick={handleZoomIn}
          >
            <Plus size={30} color='black' strokeWidth={zoomInPressed ? 3 : 2}/>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: zoomOutPressed ? '#CCFFCC' : 'white',
              borderRadius: '10px',
              width: '40px',
              height: '40px',
            }}
            onClick={handleZoomOut}
          >
            <Minus size={30} color='black' strokeWidth={zoomOutPressed ? 3 : 2}/>
          </div>
        </div>
      </Box>

      {/* Top Right Side-Panel Toggle */}
      <Box sx={{
        position: 'fixed',
        top: `${statusPanelHeight + 10}px`,
        right: 'max(8px, env(safe-area-inset-right, 8px))',
        zIndex: 1003,
      }}>
        <div
          role="button"
          tabIndex={0}
          aria-label={showSidePanel ? 'Hide dev tools' : 'Show dev tools'}
          aria-expanded={showSidePanel}
          onClick={() => setShowSidePanel(v => !v)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowSidePanel(v => !v); }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'white',
            borderRadius: '10px',
            width: '40px',
            height: '40px',
            cursor: 'pointer',
          }}
        >
          {showSidePanel
            ? <ChevronRight size={30} color='black' strokeWidth={2}/>
            : <ChevronLeft size={30} color='black' strokeWidth={2}/>}
        </div>
      </Box>

      {/* Right Side-Panel: raise/lower, go-to-start, safety */}
      {showSidePanel && (
        <Box sx={{
          position: 'fixed',
          // Anchor between top and bottom (instead of a maxHeight from 100vh):
          // 100vh overshoots the visible area on mobile (URL bar) and can go
          // negative on short windows, collapsing the panel. top+bottom keeps
          // the panel bounded and within the visible viewport (dvh = dynamic
          // viewport height, correct on mobile). The min() clamp guarantees at
          // least ~120px of panel height even when the status panel nearly
          // fills the screen (e.g. phone landscape), so it never gets pushed
          // off-screen; it overlaps the status panel's lower edge only in that
          // degenerate case.
          top: `min(${statusPanelHeight + 60}px, calc(100dvh - 194px))`,
          bottom: 'max(74px, calc(env(safe-area-inset-bottom, 0px) + 74px))',
          right: 'max(8px, env(safe-area-inset-right, 8px))',
          zIndex: 1002,
          width: { xs: 220, sm: 280 },
          overflow: 'hidden',
          backgroundColor: 'rgba(255,255,255,0.95)',
          borderRadius: '8px',
          padding: '10px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          boxSizing: 'border-box',
        }}>
          {/* Title (fixed header) */}
          <Typography sx={{
            fontSize: '1rem',
            fontWeight: 'bold',
            color: 'black',
            textAlign: 'center',
            borderBottom: '1px solid rgba(0,0,0,0.15)',
            paddingBottom: '4px',
            flexShrink: 0,
          }}>
            Dev Tools
          </Typography>

          {/* Scrollable content */}
          <Box sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}>
          {/* Raise / Lower */}
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '6px' }}>
            <Button
              variant="contained"
              onClick={() => setConfirmDialog({ open: true, action: 'raise' })}
              disabled={planterBusy || !callManualSetLowerLink}
              sx={{
                flex: 1,
                height: '40px',
                borderRadius: '5px',
                fontSize: { xs: '0.9rem', sm: '1.1rem' },
                fontWeight: 'bold',
                backgroundColor: '#1976d2',
                '&:hover': { backgroundColor: '#115293' },
              }}
            >
              RAISE
            </Button>
            <Button
              variant="contained"
              onClick={() => setConfirmDialog({ open: true, action: 'lower' })}
              disabled={planterBusy || !callManualSetLowerLink}
              sx={{
                flex: 1,
                height: '40px',
                borderRadius: '5px',
                fontSize: { xs: '0.9rem', sm: '1.1rem' },
                fontWeight: 'bold',
                backgroundColor: '#ed6c02',
                '&:hover': { backgroundColor: '#b25100' },
              }}
            >
              LOWER
            </Button>
          </Box>

          {/* Go to start */}
          <Button
            variant="contained"
            onClick={() => setConfirmDialog({ open: true, action: 'goToStart' })}
            disabled={!callGoToStart}
            sx={{
              width: '100%',
              height: '40px',
              borderRadius: '5px',
              fontSize: { xs: '0.9rem', sm: '1.1rem' },
              fontWeight: 'bold',
              backgroundColor: '#7b1fa2',
              '&:hover': { backgroundColor: '#5a157a' },
            }}
          >
            GO TO START
          </Button>

          {/* Safety section */}
          {(() => {
            const mode = safetyState?.safety_mode || '';
            const actions = safetyState?.active_actions || [];
            const errors = safetyState?.error_components || [];
            const warnings = safetyState?.warning_components || [];
            // E-stop latch: 'latched' | 'clear' | 'unknown' (went quiet) | 'none' (never received).
            // Only a fresh `true` shows the banner, and nothing here ever claims the latch is clear.
            const latchState = (estopLatched !== true && estopLatched !== false) ? 'none'
              : dataStale?.estopLatched ? 'unknown'
              : estopLatched ? 'latched' : 'clear';
            // safety_manager publishes NORMAL | DEGRADED | CRITICAL | EMERGENCY
            // (otto_msgs/SafetyState). EMERGENCY used to fall through to green.
            const modeColor =
              mode === 'EMERGENCY' || mode === 'CRITICAL' ? '#f44336'
              : mode === 'DEGRADED' || mode === 'WARNING' ? '#ed6c02'
              : mode ? '#4caf50'
              : '#888';
            const renderList = (arr) => (arr && arr.length > 0)
              ? (
                <Box component="ul" sx={{ m: 0, pl: '18px' }}>
                  {arr.map((item, i) => (
                    <Typography key={i} component="li" sx={{ fontSize: '0.8rem', color: 'black', wordBreak: 'break-word' }}>
                      {String(item)}
                    </Typography>
                  ))}
                </Box>
              )
              : <Typography sx={{ fontSize: '0.8rem', color: '#666' }}>—</Typography>;
            return (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                marginTop: '4px',
                padding: '6px 8px',
                borderRadius: '5px',
                backgroundColor: 'rgba(0,0,0,0.04)',
              }}>
                {/* Collapsible header: title + mode chip + chevron */}
                <Box
                  onClick={() => setSafetyExpanded(v => !v)}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'black' }}>Safety</Typography>
                    <Box sx={{
                      backgroundColor: modeColor,
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.75rem',
                      padding: '1px 6px',
                      borderRadius: '4px',
                    }}>
                      {mode || '—'}
                    </Box>
                  </Box>
                  {safetyExpanded
                    ? <ChevronUp size={18} color='black' strokeWidth={2} />
                    : <ChevronDown size={18} color='black' strokeWidth={2} />}
                </Box>
                {/* Outside the collapsible part so it is reachable either way:
                    the robot refuses START while an e-stop is latched, and the
                    latch is not visible in the mode chip once the fault clears. */}
                {latchState === 'latched' && (
                  <Box role="status" sx={{
                    backgroundColor: '#c62828',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: { xs: '0.85rem', sm: '1rem' },
                    textAlign: 'center',
                    padding: '6px 8px',
                    borderRadius: '5px',
                    animation: 'latchPulse 1.5s ease-in-out infinite',
                    '@keyframes latchPulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.7 },
                    },
                  }}>
                    E-STOP LATCHED — reset required
                  </Box>
                )}
                {latchState === 'unknown' && (
                  <Typography role="status" sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#666' }}>
                    E-stop latch status unknown (no update from robot)
                  </Typography>
                )}
                <Button
                  variant="contained"
                  onClick={() => setConfirmDialog({ open: true, action: 'safetyReset' })}
                  disabled={safetyResetBusy || !callSafetyReset}
                  sx={{
                    width: '100%',
                    height: '36px',
                    borderRadius: '5px',
                    fontSize: { xs: '0.85rem', sm: '1rem' },
                    fontWeight: 'bold',
                    backgroundColor: '#d84315',
                    '&:hover': { backgroundColor: '#9f2f0e' },
                  }}
                >
                  {safetyResetBusy ? 'RESETTING...' : 'SAFETY RESET'}
                </Button>
                {safetyResetResult && (
                  <Typography
                    data-testid="safety-reset-result"
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      color: safetyResetResult.outcome === 'ok' ? '#2e7d32' : '#c62828',
                      wordBreak: 'break-word',
                    }}
                  >
                    Last reset {safetyResetResult.at.toLocaleTimeString()}:{' '}
                    {safetyResetResult.outcome === 'ok' ? 'OK' : safetyResetResult.outcome === 'refused' ? 'REFUSED' : 'FAILED'}
                    {' - '}{safetyResetResult.message}
                  </Typography>
                )}
                {safetyExpanded && (
                  <>
                    {/* Reduced-speed reason. The main screen only marks the Speed
                        tile (REDUCED badge); the cause lives here. */}
                    {speedLimit === 'active' && (
                      <Box role="status" sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        backgroundColor: '#ff9800',
                        color: 'black',
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                        lineHeight: 1.2,
                        padding: '4px 8px',
                        borderRadius: '5px',
                        wordBreak: 'break-word',
                      }}>
                        <AlertTriangle size={16} style={{ flexShrink: 0 }} />
                        <span>REDUCED SPEED — safety: {speedLimitReason}</span>
                      </Box>
                    )}
                    {speedLimit === 'unknown' && (
                      <Typography role="status" sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#666' }}>
                        Speed limit status unknown (no safety update{speedReduced ? '; last known: REDUCED' : ''})
                      </Typography>
                    )}
                    <Box>
                      <Typography sx={{ fontSize: '0.75rem', color: '#444', fontWeight: 'bold' }}>Active actions</Typography>
                      <Typography sx={{ fontSize: '0.8rem', color: 'black', wordBreak: 'break-word' }}>
                        {actions.length > 0 ? actions.join(', ') : '—'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.75rem', color: '#444', fontWeight: 'bold' }}>Errors</Typography>
                      {renderList(errors)}
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.75rem', color: '#444', fontWeight: 'bold' }}>Warnings</Typography>
                      {renderList(warnings)}
                    </Box>
                  </>
                )}
              </Box>
            );
          })()}

          {/* Engine speed */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '6px 8px',
            borderRadius: '5px',
            backgroundColor: 'rgba(0,0,0,0.04)',
          }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'black' }}>
              Engine
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: 'black' }}>
                {Math.round(engineRpm)}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#444' }}>
                rpm
              </Typography>
            </Box>
          </Box>

          {/* Jetson CPU temperature — last item in the list.
              Lives here rather than in the Motors tile because that tile is 49%
              wide with a FIXED height and could not fit a fifth reading once the
              operator's text-size setting scaled it up: measured at 390px wide,
              five columns overflowed at a 1.125x text scale and by 1.5x the
              values collided into an unreadable run with cpu clipped at the tile
              edge. This panel is a normal flow column inside a scroll container,
              so the row grows with the text instead of disappearing. */}
          <Box sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: '8px',
            flexWrap: 'wrap',
            padding: '6px 8px',
            borderRadius: '5px',
            backgroundColor: 'rgba(0,0,0,0.04)',
            flexShrink: 0,
          }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'black' }}>
              CPU °C
            </Typography>
            <Typography sx={{
              fontSize: '1.1rem',
              fontWeight: 'bold',
              lineHeight: 1.2,
              color: isDataStale ? 'grey'
                : cpuTemp > 85 ? 'rgba(200, 0, 0, 1)'
                : cpuTemp > 70 ? 'rgba(170, 130, 0, 1)'
                : 'rgba(0, 140, 0, 1)',
            }}>
              {Math.round(cpuTemp)}{isDataStale ? ' (stale)' : ''}
            </Typography>
          </Box>
          </Box>
        </Box>
      )}

      {/* Heartbeat indicator - aligned with connect panel */}
      <Box sx={{
        position: 'fixed',
        bottom: 'max(16px, calc(env(safe-area-inset-bottom, 0px) + 8px))',
        left: 'max(8px, env(safe-area-inset-left, 8px))',
        zIndex: 1002,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        backgroundColor: 'rgba(0,0,0,0.7)',
        borderRadius: '10px',
        padding: '10px 16px',
        height: '52px',
        boxSizing: 'border-box',
      }}>
        <Heart
          size={28}
          color={isDataStale ? '#888' : '#ff4444'}
          fill={heartBeat ? (isDataStale ? '#888' : '#ff4444') : 'none'}
          style={{
            transition: 'transform 0.15s ease-out, fill 0.15s ease-out',
            transform: heartBeat ? 'scale(1.35)' : 'scale(1)',
          }}
        />
        <span style={{
          fontSize: '0.7rem',
          fontFamily: '"Source Sans Pro", "Roboto", "Helvetica", "Arial", sans-serif',
          color: isDataStale ? '#888' : '#4f4',
          fontWeight: 500,
          letterSpacing: '0.02857em',
          textTransform: 'uppercase',
          lineHeight: 1.75,
        }}>
          {isDataStale ? 'NO DATA' : 'LIVE'}
        </span>
      </Box>

      {/* Planter service response */}
      <Snackbar
        open={planterSnack.open}
        autoHideDuration={4000}
        onClose={handlePlanterSnackClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          zIndex: 2000,
          top: '50% !important',
          left: '50% !important',
          right: 'auto !important',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <Alert
          onClose={handlePlanterSnackClose}
          severity={planterSnack.severity}
          variant="filled"
          sx={{ fontWeight: 'bold', fontSize: '1rem' }}
        >
          {planterSnack.message}
        </Alert>
      </Snackbar>

      {/* Go-to-start service response */}
      <Snackbar
        open={goToStartSnack.open}
        autoHideDuration={4000}
        onClose={handleGoToStartSnackClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          zIndex: 2000,
          top: '50% !important',
          left: '50% !important',
          right: 'auto !important',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <Alert
          onClose={handleGoToStartSnackClose}
          severity={goToStartSnack.severity}
          variant="filled"
          sx={{ fontWeight: 'bold', fontSize: '1rem' }}
        >
          {goToStartSnack.message}
        </Alert>
      </Snackbar>

      {/* Safety-reset service response. Success fades; a refusal or failure
          stays until the operator closes it, so the reason is actually read. */}
      <Snackbar
        open={safetyResetSnack.open}
        autoHideDuration={safetyResetSnack.severity === 'error' ? null : 4000}
        onClose={handleSafetyResetSnackClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          zIndex: 2000,
          top: '50% !important',
          left: '50% !important',
          right: 'auto !important',
          transform: 'translate(-50%, -50%)',
          // The robot's refusal reason is a full sentence; without this the
          // centred popup shrinks to half the screen and wraps it a word a line.
          width: 'max-content',
          maxWidth: 'calc(100vw - 32px)',
        }}
      >
        <Alert
          onClose={handleSafetyResetSnackClose}
          severity={safetyResetSnack.severity}
          variant="filled"
          sx={{ fontWeight: 'bold', fontSize: '1rem' }}
        >
          {safetyResetSnack.message}
        </Alert>
      </Snackbar>

      {/* Confirmation Dialog */}
      {(() => {
        const dialogConfigs = {
          start:     { title: 'Start Navigation?',     body: 'Are you sure you want to start the robot navigation?',         confirmLabel: 'Start',       color: 'success' },
          stop:      { title: 'Stop Navigation?',      body: 'Are you sure you want to stop the robot navigation?',          confirmLabel: 'Stop',        color: 'error' },
          raise:     { title: 'Raise Planter?',        body: 'Are you sure you want to raise the planter?',                   confirmLabel: 'Raise',       color: 'primary' },
          lower:     { title: 'Lower Planter?',        body: 'Are you sure you want to lower the planter?',                   confirmLabel: 'Lower',       color: 'warning' },
          goToStart: { title: 'Go to Start Position?', body: 'Are you sure you want to drive the robot to the start of the path?', confirmLabel: 'Go to Start', color: 'secondary' },
          arm:       { title: 'Arm Robot?',            body: 'Are you sure you want to ARM the robot? Motors will be enabled.', confirmLabel: 'Arm',         color: 'success' },
          disarm:    { title: 'Disarm Robot?',         body: 'Are you sure you want to DISARM the robot? Motors will be disabled.', confirmLabel: 'Disarm',  color: 'error' },
          safetyReset: { title: 'Reset Safety Stop?',    body: 'This releases a latched e-stop or safety stop so the robot can move again. Only reset once the cause is fixed and the area around the robot is clear. It does not start the mission; press START for that.', confirmLabel: 'Reset', color: 'warning' },
        };
        const cfg = dialogConfigs[confirmDialog.action] || { title: '', body: '', confirmLabel: 'Confirm', color: 'primary' };
        return (
          <Dialog open={confirmDialog.open} onClose={handleCancel}>
            <DialogTitle sx={{ fontWeight: 'bold' }}>{cfg.title}</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ fontSize: '1.1rem' }}>{cfg.body}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCancel} color="inherit">Cancel</Button>
              <Button
                onClick={handleConfirm}
                variant="contained"
                color={cfg.color}
                autoFocus
              >
                {cfg.confirmLabel}
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      {isLoaded ? (
      <GoogleMap
        mapContainerStyle={{
          width: '100%',
          height: '100%',
        }}
        zoom={zoom}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onZoomChanged={onZoomChanged}
        options={mapOptions}
      >
        {/* Explicit z-order for every robot/route marker.
            A Marker with no zIndex gets an AUTO value derived from its
            latitude, and those auto values outrank small explicit ones — so
            the start-pose marker (which had none) was painting over the
            rear-axle dot whenever the robot sat near the start of the path,
            which is exactly where it parks. Ordering, bottom to top:
            start pose < rear axle < robot point < heading arrow. */}
        {isValidLatLng(firstPose) && (
          <Marker
            position={firstPose}
            icon={firstPoseIcon}
            zIndex={3000}
          />
        )}
        {rearAxlePosition && rearAxlePointIcon && (
          <Marker
            position={rearAxlePosition}
            icon={rearAxlePointIcon}
            zIndex={3001}
          />
        )}
        {robotPosition && robotPointIcon && (
          <Marker
            position={robotPosition}
            icon={robotPointIcon}
            zIndex={3002}
          />
        )}
        {robotPosition && (
          <Marker
            position={robotPosition}
            icon={icon}
            zIndex={3003}
          />
        )}
        {globalPathMap.length > 0 && <Polyline path={globalPathMap} options={dynamicOptions_yellow}/>}
        {/* The active path replaces the cyan /curr_path line (the planner's mission path, mostly a
            duplicate of the global path). Older robot builds don't publish the active path, so they
            keep the cyan line. */}
        {!activePathAvailable && currentPathMap.length > 0 && <Polyline path={currentPathMap} options={dynamicOptions_green} />}
        {activePathMap.length > 0 && <Polyline path={activePathMap} options={dynamicOptions_active} />}
      </GoogleMap>
      ) : <></>}
    </Box>
  );
};

export default MapScreen;
